import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Popconfirm,
  Typography,
  Tag,
  message,
  Card,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  LockOutlined,
} from "@ant-design/icons";
import RolePermissionsModal from "./RolePermissionsModal";
import {
  createRole,
  deleteRole,
  getAllRoles,
  updateRole,
} from "../../api/roles/roleRequest";
import PermissionRender from "../../components/PermissionRender";

const { Title, Text } = Typography;

const RolesPage = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPermissionModalVisible, setIsPermissionModalVisible] =
    useState(false);
  const [currentRole, setCurrentRole] = useState(null);
  const [form] = Form.useForm();

  // Fetch all roles when component mounts
  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const data = await getAllRoles();
      setRoles(data);
    } catch (error) {
      message.error("Failed to fetch roles");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const showModal = (role = null) => {
    setCurrentRole(role);
    form.resetFields();

    if (role) {
      form.setFieldsValue({ name: role.name });
    }

    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setCurrentRole(null);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (currentRole) {
        // Update existing role
        await updateRole(currentRole.id, values);
        message.success("Role updated successfully");
      } else {
        // Create new role
        await createRole(values);
        message.success("Role created successfully");
      }

      setIsModalVisible(false);
      setCurrentRole(null);
      fetchRoles();
    } catch (error) {
      message.error(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await deleteRole(id);
      message.success("Role deleted successfully");
      fetchRoles();
    } catch (error) {
      message.error("Failed to delete role");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const showPermissionsModal = (role) => {
    setCurrentRole(role);
    setIsPermissionModalVisible(true);
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: "10%",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => (
        <Text strong className="text-blue-700">
          {text}
        </Text>
      ),
    },
    {
      title: "Permissions",
      dataIndex: "permissions",
      key: "permissions",
      render: (permissions) => (
        <>
          {permissions && permissions.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {permissions.slice(0, 3).map((permission) => (
                <Tag
                  color="blue"
                  key={permission.id}
                  className="px-2 py-1 text-xs rounded-lg"
                >
                  {permission.resource}.{permission.action}
                </Tag>
              ))}
              {permissions.length > 3 && (
                <Tag color="default" className="px-2 py-1 text-xs rounded-lg">
                  +{permissions.length - 3} more
                </Tag>
              )}
            </div>
          ) : (
            <Text type="secondary" italic>
              No permissions assigned
            </Text>
          )}
        </>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: "20%",
      render: (_, record) => (
        <Space>
          <PermissionRender requiredPermission={["role", "update"]}>
            <Tooltip title="Edit Role">
              <Button
                type="primary"
                icon={<EditOutlined />}
                size="small"
                onClick={() => showModal(record)}
                className="bg-blue-600"
              />
            </Tooltip>
          </PermissionRender>
          <PermissionRender requiredPermission={["role", "update"]}>
            <Tooltip title="Manage Permissions">
              <Button
                type="default"
                icon={<LockOutlined />}
                size="small"
                onClick={() => showPermissionsModal(record)}
                className="bg-purple-100 text-purple-700 border-purple-300 hover:bg-purple-200"
              />
            </Tooltip>
          </PermissionRender>
          <PermissionRender requiredPermission={["role", "delete"]}>
            <Popconfirm
              title="Are you sure you want to delete this role?"
              icon={<ExclamationCircleOutlined style={{ color: "red" }} />}
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Popconfirm>
          </PermissionRender>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <Title level={2} className="m-0">
          <LockOutlined className="mr-2 text-blue-600" /> Role Management
        </Title>
        <PermissionRender requiredPermission={["role", "create"]}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showModal()}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Add Role
          </Button>
        </PermissionRender>
      </div>

      <Card className="shadow-md">
        <Table
          columns={columns}
          dataSource={roles}
          rowKey="id"
          loading={loading}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            showTotal: (total) => `Total: ${total} roles`,
          }}
          className="bg-white rounded-lg"
        />
      </Card>

      <Modal
        title={currentRole ? "Edit Role" : "Add Role"}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleSubmit}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {currentRole ? "Update" : "Create"}
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Role Name"
            rules={[{ required: true, message: "Please enter role name" }]}
          >
            <Input placeholder="Enter role name" />
          </Form.Item>
        </Form>
      </Modal>

      {currentRole && (
        <RolePermissionsModal
          visible={isPermissionModalVisible}
          role={currentRole}
          onClose={() => {
            setIsPermissionModalVisible(false);
            fetchRoles(); // Refresh the roles after permissions update
          }}
        />
      )}
    </div>
  );
};

export default RolesPage;
